import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import Header from 'shared/components/Header';
import LoadingSpinner from 'shared/components/LoadingSpinner';
import Table from 'shared/components/Table';
import { tokens } from 'shared/theme';
import useTenantUserService from 'shared/api/services/tenantUserService';
import useTenantsService from 'shared/api/services/tenantsService';
import CreateTenantUsers from './CreateTenantUsers';
import EditTenantUserForAdmin from './EditTenantUserForAdmin';
import { useAuth } from 'shared/hooks/useAuth';
import { canViewAllRecords } from 'shared/utils/canViewAllRecords';
import { buildUserDetailsPath, getTenantIdFromWindowLocation, isTenantUuid } from 'shared/utils/tenantRoute';
import { resolveRegistryTenantId } from 'shared/api/resolveRegistryTenantId';
import { getLastLoginTenantId } from 'shared/utils/lastTenantLogin';
import { getRegistryTenantIdFromSession } from 'shared/utils/sessionProfileStore';

const ADMIN_TENANT_USERS_STORAGE_KEY = 'platformAdminRegistryTenantId';

function resolveAdminRegistryTenantId(stateTenantId) {
  if (stateTenantId != null && String(stateTenantId).trim() !== '') {
    return String(stateTenantId).trim();
  }
  try {
    const stored = sessionStorage.getItem(ADMIN_TENANT_USERS_STORAGE_KEY);
    if (stored != null && stored.trim() !== '') {
      return stored.trim();
    }
  } catch {
    /* ignore */
  }
  const fromSession = getRegistryTenantIdFromSession();
  if (fromSession && isTenantUuid(fromSession)) {
    return fromSession;
  }
  const fromLastLogin = getLastLoginTenantId();
  if (fromLastLogin && isTenantUuid(fromLastLogin)) {
    return fromLastLogin;
  }
  const resolved = resolveRegistryTenantId();
  if (resolved && isTenantUuid(resolved)) {
    return resolved;
  }
  // Last resort: route slug (e.g. datahouse) — backend X-TenantID accepts name.
  const fromRoute = getTenantIdFromWindowLocation();
  return fromRoute || resolved || '';
}

const TenantUsersForAdmin = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const location = useLocation();
  const stateTenantId =
    location.state?.registryTenantId ?? location.state?.tenantId ?? null;
  const registryTenantId = useMemo(
    () => resolveAdminRegistryTenantId(stateTenantId),
    [stateTenantId]
  );
  const { user } = useAuth();
  const canViewAll = canViewAllRecords(user);
  const { getTenantById } = useTenantsService();
  const [tenantDetails, setTenantDetails] = useState(null);
  const [tenantLoading, setTenantLoading] = useState(false);

  const tenantName =
    tenantDetails?.name ?? location.state?.tenantName ?? user?.data?.tenantName ?? '';

  const {
    users,
    totalRecords,
    fetchTenantUsers,
    createTenantUser,
    getTenantUser,
    updateTenantUser,
    deleteTenantUser,
    isLoading,
  } = useTenantUserService(registryTenantId);

  const [page, setPage] = useState(0);
  const [size] = useState(10);
  const [showAll, setShowAll] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!registryTenantId) return;
    try {
      sessionStorage.setItem(ADMIN_TENANT_USERS_STORAGE_KEY, registryTenantId);
    } catch {
      /* ignore storage errors */
    }
  }, [registryTenantId]);

  useEffect(() => {
    if (!registryTenantId) return;
    fetchTenantUsers(page, size, canViewAll && showAll ? null : 'ACTIVE');
  }, [registryTenantId, page, size, showAll, canViewAll, fetchTenantUsers]);

  useEffect(() => {
    setPage(0);
  }, [registryTenantId]);

  useEffect(() => {
    if (!registryTenantId) return;
    if (!isTenantUuid(registryTenantId)) {
      setTenantDetails(null);
      setTenantLoading(false);
      return undefined;
    }
    let active = true;
    setTenantLoading(true);
    (async () => {
      try {
        const details = await getTenantById(registryTenantId);
        if (active) {
          setTenantDetails(details ?? null);
        }
      } catch (error) {
        if (active) {
          setTenantDetails(null);
        }
        const err =
          error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          'Failed to load tenant details';
        toast.error(typeof err === 'string' ? err : 'Failed to load tenant details');
      } finally {
        if (active) {
          setTenantLoading(false);
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [registryTenantId, getTenantById]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const refreshList = useCallback(() => {
    if (!registryTenantId) return;
    fetchTenantUsers(page, size, canViewAll && showAll ? null : 'ACTIVE');
  }, [registryTenantId, fetchTenantUsers, page, size, showAll, canViewAll]);

  const handleView = useCallback(
    (row) => {
      if (!row?.id || !registryTenantId) return;
      navigate(buildUserDetailsPath(row.id, registryTenantId), {
        state: { registryTenantId, tenantName },
      });
    },
    [navigate, registryTenantId, tenantName]
  );
  const handleEdit = useCallback((row) => setEditRow(row), []);
  const handleDeleteClick = useCallback((row) => setDeleteTarget(row), []);

  const confirmDelete = async () => {
    if (!deleteTarget?.id) return;
    setDeleting(true);
    try {
      await deleteTenantUser(deleteTarget.id);
      toast.success('User marked as deleted');
      setDeleteTarget(null);
      refreshList();
    } catch (error) {
      const err =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to delete user';
      toast.error(typeof err === 'string' ? err : 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  const columns = useMemo(
    () => [
      { field: 'name', headerName: 'Name', flex: 1, minWidth: 140 },
      { field: 'email', headerName: 'Email', flex: 1.2, minWidth: 180 },
      { field: 'phoneNumber', headerName: 'Phone', flex: 1, minWidth: 120 },
      {
        field: 'jobPosition',
        headerName: 'Job Position',
        flex: 1,
        minWidth: 140,
        valueGetter: (params) =>
          params.row?.jobPosition || params.row?.job_position || '',
      },
      { field: 'tenantName', headerName: 'Tenant', flex: 1, minWidth: 140 },
      { field: 'role', headerName: 'Role', width: 110 },
      { field: 'status', headerName: 'Status', width: 110 },
      { field: 'createdAt', headerName: 'Created', flex: 1, minWidth: 160 },
      
      {
        field: 'actions',
        headerName: 'Actions',
        width: 150,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <Box display="flex" alignItems="center" gap={0.25}>
            <Tooltip title="View">
              <IconButton
                size="small"
                color="success"
                onClick={() => handleView(row)}
                aria-label="View user"
              >
                <VisibilityOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {/* <Tooltip title="Edit">
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleEdit(row)}
                aria-label="Edit user"
              >
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip> */}
            <Tooltip title="Delete">
              <IconButton
                size="small"
                color="error"
                onClick={() => handleDeleteClick(row)}
                aria-label="Delete user"
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [handleView, handleEdit, handleDeleteClick]
  );

  /**
   * Safety guard on UI: only show users that belong to the tenant from route param.
   * Backend already supports tenantId filtering; this keeps UI scoped even if mixed data is returned.
   */
  const tenantScopedUsers = useMemo(() => {
    if (!registryTenantId) return [];
    const list = Array.isArray(users) ? users : [];
    // List API is already tenant-scoped via X-TenantID; only tighten when both sides are UUIDs.
    if (!isTenantUuid(registryTenantId)) {
      return list;
    }
    return list.filter((u) => {
      const rowTenantId = u?.tenantId != null ? String(u.tenantId).trim() : '';
      if (!rowTenantId) return true;
      return !isTenantUuid(rowTenantId) || rowTenantId === registryTenantId;
    });
  }, [users, registryTenantId]);

  if (!registryTenantId) {
    return (
      <Box m="20px">
        <Typography color="error" sx={{ mb: 2 }}>
          No tenant was selected. Open this page from Tenants (eye icon).
        </Typography>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/tenants')}
        >
          Back to tenants
        </Button>
      </Box>
    );
  }

  return (
    <Box m="20px">
      <EditTenantUserForAdmin
        open={Boolean(editRow)}
        onClose={() => setEditRow(null)}
        row={editRow}
        registryTenantId={registryTenantId}
        tenantNameDefault={tenantName}
        updateTenantUser={updateTenantUser}
        onSuccess={refreshList}
      />
      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => !deleting && setDeleteTarget(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete user?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">Do you want to delete the user?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDelete}
            disabled={deleting}
          >
            {deleting ? 'Deleting…' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      <CreateTenantUsers
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSuccess={refreshList}
        createTenantUser={createTenantUser}
        tenantName={tenantName}
      />

      <Header title={`${tenantName ? `${tenantName} ` : ''}Tenant users`} />

      <Box
        mb={2}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        flexWrap="wrap"
      >
        {canViewAll ? (
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Button
            color="secondary"
            variant={showAll ? 'contained' : 'outlined'}
            size="small"
            sx={{ fontSize: '13px', py: 0.75, px: 1.5 }}
            onClick={() => {
              setShowAll(true);
              setPage(0);
            }}
          >
            View all
          </Button>
          <Button
            color="secondary"
            variant={!showAll ? 'contained' : 'outlined'}
            size="small"
            sx={{ fontSize: '13px', py: 0.75, px: 1.5 }}
            onClick={() => {
              setShowAll(false);
              setPage(0);
            }}
          >
            Active only
          </Button>
        </Box>
        ) : (
          <Box />
        )}

        <Button
          color="secondary"
          variant="contained"
          size="small"
          sx={{ fontSize: '13px', py: 0.75, px: 1.5 }}
          onClick={() => setCreateOpen(true)}
        >
          Create user
        </Button>
      </Box>

      {isLoading || tenantLoading ? (
        <LoadingSpinner message="Loading tenant users…" height="300px" />
      ) : (
        <Table
          tableId="platform.tenant-users-admin"
          title={`${tenantName ? `${tenantName} ` : ''}Tenant users`}
          rows={tenantScopedUsers}
          columns={columns}
          totalRecords={tenantScopedUsers.length}
          size={size}
          page={page}
          handlePageChange={handlePageChange}
          colors={colors}
        />
      )}
    </Box>
  );
};

export default TenantUsersForAdmin;
